const SHEET_SELECTOR = '[data-msc-sheet]';
const SCENE_SELECTOR = '.msc-sheet__scene';
const BACK_FACE_SELECTOR = '.msc-sheet__face--back';

const IDLE_YAW_DEGREES = 7;
const IDLE_PITCH_DEGREES = 3.5;
const IDLE_SPEED = 0.00022;
const POINTER_YAW_DEGREES = 16;
const POINTER_PITCH_DEGREES = 11;
const POINTER_SPEED_GAIN = 0.00004;
const MAX_IDLE_SPEED = 0.0016;
const SPEED_DECAY = 0.94;
const SPRING = 0.12;
const SETTLED_DEGREES = 0.05;

const cards = new Map();

let frameId = null;
let visibilityObserver = null;
let lastFrameTime = 0;

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function ensureObserver() {
    if (visibilityObserver || typeof IntersectionObserver !== 'function') {
        return;
    }
    visibilityObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            const card = cards.get(entry.target);
            if (card) {
                card.visible = entry.isIntersecting;
            }
        });
        startLoop();
    });
}

function isSettled(card) {
    return Math.abs(card.yaw - card.yawTarget) < SETTLED_DEGREES
        && Math.abs(card.pitch - card.pitchTarget) < SETTLED_DEGREES;
}

function needsFrame(card) {
    if (!card.visible || !card.el.isConnected) {
        return false;
    }
    if (card.reduceMotion) {
        return !isSettled(card);
    }
    return true;
}

function readIdleDrift(card, elapsed) {
    card.phase += elapsed * card.speed;
    card.speed = Math.max(IDLE_SPEED, card.speed * SPEED_DECAY);

    return {
        yaw: Math.sin(card.phase) * IDLE_YAW_DEGREES,
        pitch: Math.sin(card.phase * 0.6) * IDLE_PITCH_DEGREES,
    };
}

function updateTargets(card, elapsed) {
    if (card.reduceMotion) {
        card.yawTarget = card.flipTurns * 180;
        card.pitchTarget = 0;
        return;
    }

    if (card.pointerActive) {
        card.yawTarget = card.flipTurns * 180 + card.pointerYaw;
        card.pitchTarget = card.pointerPitch;
        return;
    }

    const drift = readIdleDrift(card, elapsed);
    card.yawTarget = card.flipTurns * 180 + drift.yaw;
    card.pitchTarget = drift.pitch;
}

function renderCard(card) {
    card.scene.style.transform =
        `rotateX(${card.pitch.toFixed(2)}deg) rotateY(${card.yaw.toFixed(2)}deg)`;
}

function step(now) {
    frameId = null;
    const elapsed = lastFrameTime === 0 ? 16 : Math.min(now - lastFrameTime, 64);
    lastFrameTime = now;

    cards.forEach(function (card) {
        if (!needsFrame(card)) {
            return;
        }
        updateTargets(card, elapsed);
        card.yaw += (card.yawTarget - card.yaw) * SPRING;
        card.pitch += (card.pitchTarget - card.pitch) * SPRING;
        renderCard(card);
    });

    startLoop();
}

function startLoop() {
    if (frameId !== null) {
        return;
    }
    let animating = false;
    cards.forEach(function (card) {
        animating = animating || needsFrame(card);
    });
    if (!animating) {
        lastFrameTime = 0;
        return;
    }
    frameId = requestAnimationFrame(step);
}

function handlePointerMove(event) {
    const card = cards.get(event.currentTarget);
    if (!card) {
        return;
    }
    const rect = card.el.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    const travel = Math.abs(x - card.pointerX) + Math.abs(y - card.pointerY);

    card.pointerX = x;
    card.pointerY = y;
    card.pointerYaw = x * POINTER_YAW_DEGREES * 2;
    card.pointerPitch = -y * POINTER_PITCH_DEGREES * 2;
    card.speed = Math.min(MAX_IDLE_SPEED, card.speed + travel * POINTER_SPEED_GAIN * 1000);
    startLoop();
}

function handlePointerEnter(event) {
    const card = cards.get(event.currentTarget);
    if (!card || card.reduceMotion) {
        return;
    }
    card.pointerActive = true;
    card.el.addEventListener('pointermove', handlePointerMove);
    startLoop();
}

function handlePointerLeave(event) {
    const card = cards.get(event.currentTarget);
    if (!card) {
        return;
    }
    card.pointerActive = false;
    card.el.removeEventListener('pointermove', handlePointerMove);
    startLoop();
}

function handleKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
        return;
    }
    event.preventDefault();
    event.currentTarget.click();
}

function registerCard(element) {
    if (cards.has(element)) {
        return;
    }
    const scene = element.querySelector(SCENE_SELECTOR);
    if (!scene) {
        return;
    }

    cards.set(element, {
        el: element,
        scene,
        reduceMotion: prefersReducedMotion(),
        visible: true,
        yaw: 0,
        pitch: 0,
        yawTarget: 0,
        pitchTarget: 0,
        flipTurns: 0,
        phase: Math.random() * Math.PI * 2,
        speed: IDLE_SPEED,
        pointerActive: false,
        pointerX: 0,
        pointerY: 0,
        pointerYaw: 0,
        pointerPitch: 0,
    });

    element.addEventListener('pointerenter', handlePointerEnter);
    element.addEventListener('pointerleave', handlePointerLeave);
    if (element.hasAttribute('data-action')) {
        element.addEventListener('keydown', handleKeyDown);
    }
    ensureObserver();
    if (visibilityObserver) {
        visibilityObserver.observe(element);
    }
    startLoop();
}

function releaseCard(element) {
    const card = cards.get(element);
    if (!card) {
        return;
    }
    element.removeEventListener('pointerenter', handlePointerEnter);
    element.removeEventListener('pointerleave', handlePointerLeave);
    element.removeEventListener('pointermove', handlePointerMove);
    element.removeEventListener('keydown', handleKeyDown);
    if (visibilityObserver) {
        visibilityObserver.unobserve(element);
    }
    cards.delete(element);
}

export function syncMiSheberachCards(root) {
    const scope = root || document;
    scope.querySelectorAll(SHEET_SELECTOR).forEach(registerCard);
    cards.forEach(function (card, element) {
        if (!element.isConnected) {
            releaseCard(element);
        }
    });
}

export function flipMiSheberachCard(container) {
    const element = container ? container.querySelector(SHEET_SELECTOR) : null;
    if (!element || !element.querySelector(BACK_FACE_SELECTOR)) {
        return;
    }
    registerCard(element);
    const card = cards.get(element);
    card.flipTurns += 1;
    card.speed = MAX_IDLE_SPEED;
    startLoop();
}

export function resetMiSheberachCard(container) {
    const element = container ? container.querySelector(SHEET_SELECTOR) : null;
    const card = element ? cards.get(element) : null;
    if (!card) {
        return;
    }
    card.flipTurns = 0;
    card.yaw = 0;
    card.pitch = 0;
    card.speed = IDLE_SPEED;
    renderCard(card);
    startLoop();
}

export function isMiSheberachCardFlipped(container) {
    const element = container ? container.querySelector(SHEET_SELECTOR) : null;
    const card = element ? cards.get(element) : null;

    return card ? card.flipTurns % 2 !== 0 : false;
}
